import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../api/authApi';

const initialState = {
  user: null,
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  pendingEmail: null,
  otpType: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, token } = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      localStorage.setItem('token', token);
    },
    updateUser: (state, action) => {
      // Merge partial fields into the current user without replacing the whole
      // object (preserves role/token-derived fields). Used after a profile save.
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    // MER-02: flip the current user to suspended (e.g. after a 403 ACCOUNT_SUSPENDED
    // mid-session) so the global blocking popup shows without a re-login.
    setSuspended: (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          status: 'suspended',
          suspensionReason: action.payload?.reason ?? state.user.suspensionReason ?? null,
        };
      }
    },
    setPendingAuth: (state, action) => {
      const { email, type } = action.payload;
      state.pendingEmail = email;
      state.otpType = type;
    },
    clearPendingAuth: (state) => {
      state.pendingEmail = null;
      state.otpType = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.pendingEmail = null;
      state.otpType = null;
      localStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        authApi.endpoints.verifyRegistration.matchFulfilled,
        (state, { payload }) => {
          state.user = payload.data.user;
          state.token = payload.data.token;
          state.isAuthenticated = true;
          state.pendingEmail = null;
          state.otpType = null;
          localStorage.setItem('token', payload.data.token);
        }
      )
      // "Trust this device": a trusted device logs in at /auth/login without an
      // OTP round-trip, so the token arrives on the login response itself.
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          if (!payload.data?.token) return; // untrusted device → OTP still pending
          state.user = payload.data.user;
          state.token = payload.data.token;
          state.isAuthenticated = true;
          state.pendingEmail = null;
          state.otpType = null;
          localStorage.setItem('token', payload.data.token);
        }
      )
      .addMatcher(
        authApi.endpoints.verifyLogin.matchFulfilled,
        (state, { payload }) => {
          // When 2FA is required the response carries no token; do not
          // authenticate yet (the verifyLogin2FA step completes the login).
          if (!payload.data?.token) return;
          state.user = payload.data.user;
          state.token = payload.data.token;
          state.isAuthenticated = true;
          state.pendingEmail = null;
          state.otpType = null;
          localStorage.setItem('token', payload.data.token);
          // Persist the device-trust token so future logins can skip the OTP.
          if (payload.data.deviceToken) {
            localStorage.setItem('maxelpay_device_token', payload.data.deviceToken);
          }
        }
      )
      .addMatcher(
        authApi.endpoints.verifyLogin2FA.matchFulfilled,
        (state, { payload }) => {
          state.user = payload.data.user;
          state.token = payload.data.token;
          state.isAuthenticated = true;
          state.pendingEmail = null;
          state.otpType = null;
          localStorage.setItem('token', payload.data.token);
          if (payload.data.deviceToken) {
            localStorage.setItem('maxelpay_device_token', payload.data.deviceToken);
          }
        }
      )
      .addMatcher(
        authApi.endpoints.googleAuth.matchFulfilled,
        (state, { payload }) => {
          // No token means the backend sent a login OTP instead (the normal
          // path). Only a trusted device gets a token here.
          if (!payload.data?.token) return;
          state.user = payload.data.user;
          state.token = payload.data.token;
          state.isAuthenticated = true;
          state.pendingEmail = null;
          state.otpType = null;
          localStorage.setItem('token', payload.data.token);
        }
      )
      .addMatcher(
        authApi.endpoints.getMe.matchFulfilled,
        (state, { payload }) => {
          state.user = payload.data.user;
        }
      );
  },
});

export const { setCredentials, updateUser, setSuspended, setPendingAuth, clearPendingAuth, logout } = authSlice.actions;
export default authSlice.reducer;
